import {
  TIMEFRAME_FILTER,
  type CardStub,
  type DashboardSection,
  type Timeframe,
} from '@/features/dashboard/api/schemas'

// A dive's KPI cards are hoisted into a row above its charts.
const KPI_DISPLAYS = new Set(['smartscalar', 'scalar'])

/**
 * A figure rather than a chart. A deep dive that says how it draws a card (a
 * derived line over a Metabase scalar, say) overrides the Metabase display.
 */
export const isKpi = (stub: CardStub) =>
  stub.chart?.chartType
    ? stub.chart.chartType === 'kpi'
    : KPI_DISPLAYS.has(stub.display)

/** Drawn as a table rather than a chart. */
export const isTableStub = (stub: CardStub) =>
  stub.chart?.chartType === 'table' || stub.display === 'table'

/** The deep dive with the id; none for another kind of section. */
export const deepDiveById = (
  sections: DashboardSection[],
  id: string | undefined
) =>
  sections.find(
    candidate => candidate.id === id && candidate.kind === 'deep_dive'
  )

/**
 * Finds the deep dive a card opens: the one the backend links it to, else the
 * first that features it. Indexes the sections once, for many lookups.
 */
const deepDiveLookup = (sections: DashboardSection[]) => {
  const byId = new Map<string, DashboardSection>()
  const byCardId = new Map<number, DashboardSection>()
  for (const dive of sections) {
    if (dive.kind !== 'deep_dive') continue
    if (!byId.has(dive.id)) byId.set(dive.id, dive)
    for (const card of dive.cards) {
      if (!byCardId.has(card.cardId)) byCardId.set(card.cardId, dive)
    }
  }
  return (stub: CardStub): DashboardSection | undefined =>
    (stub.deepDiveId ? byId.get(stub.deepDiveId) : undefined) ??
    byCardId.get(stub.cardId)
}

export const deepDiveOf = (
  sections: DashboardSection[],
  stub: CardStub
): DashboardSection | undefined => deepDiveLookup(sections)(stub)

/**
 * The home card that opens a deep dive (its stub's `deepDiveId` names it), with
 * the section it sits in, whose timeframe it follows. The deep dive's own
 * section is not searched. None when no home card points at the deep dive.
 */
export const homeCardOf = (
  sections: DashboardSection[],
  deepDiveId: string
): { stub: CardStub; section: DashboardSection } | undefined => {
  for (const section of sections) {
    if (section.kind === 'deep_dive') continue
    const stub = section.cards.find(card => card.deepDiveId === deepDiveId)
    if (stub) return { stub, section }
  }
  return undefined
}

/** The home section whose deep dives take the market filter. */
const MARKET_SECTION_ID = 'customers'

/**
 * Whether a deep dive takes the market filter: only those of the Customers
 * section, found by the section its home card sits in.
 */
export const deepDiveTakesMarkets = (
  sections: DashboardSection[],
  deepDiveId: string
) => homeCardOf(sections, deepDiveId)?.section.id === MARKET_SECTION_ID

/** Whether some card of the section takes the filter. */
export const sectionTakes = (section: DashboardSection, filter: string) =>
  section.cards.some(card => card.filters?.includes(filter))

/**
 * A deep dive's title in sentence case, keeping acronyms: 'Revenue Deep Dive'
 * reads 'Revenue deep dive', 'PAU Deep Dive' 'PAU deep dive'.
 */
export const deepDiveLabel = (title: string): string =>
  title.replace(
    / (?![A-Z]{2,}\b)(\w+)/g,
    (_, word: string) => ` ${word.toLowerCase()}`
  )

/** The timeframes whose charts carry the year-end outlook (the others show actuals). */
const OUTLOOK_TIMEFRAMES: Timeframe[] = ['YTD', 'Annual']

/**
 * What a section's cards are drawn as, under its title: 'Outlook • YTD',
 * 'Actuals • Monthly'. None for a section whose cards don't take the
 * timeframe.
 */
export const sectionSubtitle = (
  section: DashboardSection,
  timeframe: Timeframe | undefined
): string | undefined =>
  timeframe !== undefined && sectionTakes(section, TIMEFRAME_FILTER)
    ? `${OUTLOOK_TIMEFRAMES.includes(timeframe) ? 'Outlook' : 'Actuals'} • ${timeframe}`
    : undefined
